import React, { useEffect, useState } from 'react';
import { ICompliance } from '../../../@types/model/compliance/compliance';
import { ProjectDialog } from '@zz2/zz2-ui';
import { Button, TextField } from '@mui/material';
import { generalShowErrorSnackbar } from '../../../store/general/Functions';
import { getAgreementCode, getTUMToken } from '../../../services/inspectionService';
import { ITURTokenResponse } from '../../../@types/model/compliance/titan-TUR/turTokenResponse';
import { ICommodity } from '../../../@types/model/masterData/commodity/commodity';
import { ICountry } from '../../../@types/model/masterData/country/country';
import FileSaver from 'file-saver';
import moment from 'moment';
import { DATETIME_SECONDS_PALTRACK_FILE } from '../../../appConstants';
import { IStock } from '../../../@types/model/stock/stock';
import { mapComplianceToConsignment, mapConsignmentsToELot } from '../../../services/compliance/documentService';
import { IConsignmentTokenResponse } from '../../../@types/model/compliance/titan-TUR/consignmentTokenResponse';

interface IComplianceIntegrationDetailDialogProps {
    selectedCompliance : ICompliance;
    isOpen : boolean;
    ppecbToken : IConsignmentTokenResponse | null;
    onClose : () => void;
    setObtainingTokensStatus ?: (obtainingTokensStatus : 'unstarted' | 'loading' | 'completed' | 'failed') => void;
    commodities : Array<ICommodity>;
    countries : Array<ICountry>;
    stocks : Array<IStock>;
}

const ComplianceIntegrationDetailDialog = (props : IComplianceIntegrationDetailDialogProps) : React.ReactElement => {
    
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [tumToken, setTumToken] = useState<ITURTokenResponse | null>(null);

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        loadData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const loadData = async () => {
        setIsLoading(true);
        try {
            const result = await getTUMToken(props.selectedCompliance.organizationId);

            if (result) {
                setTumToken(result);
            }
        } catch (ex) {
            generalShowErrorSnackbar('Could not find TUR token');
        } finally {
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const downloadIntegrationJSON = async () => {
        if (!tumToken) {
            generalShowErrorSnackbar('Could not find TUR token');

            if (props.setObtainingTokensStatus) {
                props.setObtainingTokensStatus('failed');
            }

            return;
        }

        const commodity = props.commodities.find(x => x.id === props.selectedCompliance.commodityId);
        const country = props.countries.find(x => x.id === props.selectedCompliance.commodityId);

        const currentAgreementCodeRequestFile = new Blob([`commodityCode: ${commodity?.code}, countryCode: ${country?.code}`]);


        FileSaver.saveAs(currentAgreementCodeRequestFile, `agreement_code_request_${moment(moment.now()).format(DATETIME_SECONDS_PALTRACK_FILE)}.json`);

        try {
            const agreementCodeResult = await getAgreementCode(commodity?.code ?? '', country?.code ?? '');
            const agreementCode = agreementCodeResult.data;
            const consignment = mapComplianceToConsignment(props.selectedCompliance, agreementCode);
            const eLot = mapConsignmentsToELot([consignment], agreementCode, tumToken.access_token);

            const currentTURRequestFile = new Blob([JSON.stringify(eLot)]);
            const currentPPECBRequestFile = new Blob([JSON.stringify(consignment)]);

            FileSaver.saveAs(currentTURRequestFile, `tum_request_${moment(moment.now()).format(DATETIME_SECONDS_PALTRACK_FILE)}.json`);
            FileSaver.saveAs(currentPPECBRequestFile, `ppecb_request_${moment(moment.now()).format(DATETIME_SECONDS_PALTRACK_FILE)}.json`);
        } catch (ex) {
            generalShowErrorSnackbar('An error occurred while attempting to down the integration details.');
        }
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <ProjectDialog
            title={'Integration Details'}
            isLoadingCircular={isLoading}
            isOpen={props.isOpen}
            onClose={props.onClose}
            fullScreen
        >
            <TextField
                value={
                    '-- Tokens --\n\n' +
                    `PPECB Token: ${props.ppecbToken?.token ?? 'PPECB Token Not Found'}\n` +
                    `TUM Token: ${tumToken?.access_token ?? 'TUM Token Not Found'}\n\n\n` +

                    '-- Endpoints --\n\n' +
                    `TUM Token Request: ${API_URL}/v1/Consignment/TURToken  ==>  https://uas.ecert.co.za/oauth2/token\n` +
                    `PPECB Token Request: ${PPECB_API_URL}oauth/ApiAuth\n` +
                    `Agreement Code Request: ${API_URL}/v1/Consignment/AgreementCode  ==>  https://app.ecert.co.za/api/v1/Agreement/Get\n` +
                    `TUM Submit Request: ${API_URL}/v1/Consignment/PostTUMELot  ==>  http://${TUM_API_URL}TrackingUnit/eLot\n` +
                    `PPECB Submit Request: ${PPECB_API_URL}pi/ProductInspection/consignment\n` +
                    `PPECB Get Status Request: ${PPECB_API_URL}pi/ProductInspection/inspectionResult\n`
                }
                multiline
                variant={'outlined'}
                disabled
                className={'mnw800 mxh800 m5 ml20 mr20'}
            />
            <div className={'fdr m10 jcfe'}>
                <Button className={'m5'} variant='contained' color='primary' onClick={downloadIntegrationJSON}>
                    Download Json
                </Button>
                <div className={'flx1'} />
                <Button className={'m5'} variant='contained' color='primary' onClick={props.onClose}>
                    Ok
                </Button>
            </div>
        </ProjectDialog>
    );
};

export default ComplianceIntegrationDetailDialog;